'use client';

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { TOKEN_KEY } from '@/consts/const';
import type { AuthData } from '@/types/common';
import { clearImpersonatingFlag } from '@/utils/auth/impersonation';
import { configureRefreshAccessToken, refreshAccessToken } from '@/utils/auth/refreshAccessToken';
import { saveAuthToken } from '@/utils/auth/saveAuthToken';
import { getAppCrmLogoutPath, getCrmLogoutUrl } from '@/utils/crm/crmUrls';

type AuthProviderType = {
  auth: null | AuthData;
  setAuth: (auth: AuthData | null) => void;
  handleLoginSuccess: (token: string) => void;
  signOut: VoidFunction;
  refreshToken: () => Promise<void>;
};

const AuthProviderContext = React.createContext({} as AuthProviderType);

interface Props {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: Props) => {
  const [auth, setAuth] = useState<AuthData | null>(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    setAuth({ token });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    clearImpersonatingFlag();
    setAuth({ token: null });
    window.location.assign(getAppCrmLogoutPath(getCrmLogoutUrl()));
  }, []);

  const saveAuthData = useCallback((data: { token: string }) => {
    const { token } = data;
    setAuth({ token });
    saveAuthToken(token);
  }, []);

  useEffect(() => {
    configureRefreshAccessToken({
      signOut,
      onTokenRefreshed: (token) => setAuth({ token }),
    });
  }, [signOut]);

  const refreshToken = useCallback(async () => {
    await refreshAccessToken();
  }, []);

  const handleLoginSuccess = useCallback(
    (token: string) => {
      saveAuthData({ token });
    },
    [saveAuthData]
  );

  return (
    <AuthProviderContext.Provider
      value={{
        auth,
        setAuth,
        handleLoginSuccess,
        signOut,
        refreshToken,
      }}
    >
      {children}
    </AuthProviderContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthProviderContext);
};
